import { type RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useMediaQuery } from '@eneco/sparky/hooks';
import { type Fields } from '@sitecore/types/OfferStep';

import { checkOfferTypes } from '../../../../shared/helpers/misc';
import { Offer } from '../../../../shared/types';

interface UseOfferLayoutParams {
  availableOffers: Offer[];
  offers: Offer[];
  textualData?: Fields;
  productFilter: string;
  scrollBehavior: 'layout-effect' | 'effect';
  scrollBlock: ScrollLogicalPosition;
}

export function useOfferLayout({
  availableOffers,
  offers,
  textualData,
  productFilter,
  scrollBehavior,
  scrollBlock,
}: UseOfferLayoutParams) {
  const cardRef = useRef<HTMLDivElement | null>(null);

  const isLargeBreakpoint = useMediaQuery('lg');
  const isMediumBreakpoint = useMediaQuery('md');
  const isExtraLargeBreakpoint = useMediaQuery('xl');

  const [isPriceDetailsOpen, setIsPriceDetailsOpen] = useState(false);

  const shouldShowCarousel = !(
    isLargeBreakpoint ||
    (!isLargeBreakpoint && availableOffers.length < 2) ||
    (isMediumBreakpoint && availableOffers.length < 3) ||
    availableOffers.length === 1
  );
  const shouldShowCarouselIndicators = shouldShowCarousel && (!isExtraLargeBreakpoint || availableOffers.length > 3);
  const shouldAlignCarouselItems = shouldShowCarousel && isExtraLargeBreakpoint && availableOffers.length === 3;
  const shouldAttachCardRef = (availableOffers.length === 1 && !isMediumBreakpoint) || shouldShowCarousel;

  // Scroll to card on product filter change (layout-effect)
  useLayoutEffect(() => {
    if (scrollBehavior !== 'layout-effect') return;
    if (!shouldAttachCardRef) return;
    if (!cardRef.current) return;

    cardRef.current.scrollIntoView({ behavior: 'smooth', block: scrollBlock });
  }, [productFilter, shouldAttachCardRef, scrollBehavior, scrollBlock]);

  // Scroll to card on product filter change (effect)
  useEffect(() => {
    if (scrollBehavior !== 'effect') return;
    if (!cardRef.current) return;

    cardRef.current.scrollIntoView({ behavior: 'smooth', block: scrollBlock });
  }, [productFilter, scrollBehavior, scrollBlock]);

  // Layout computations
  const filteredOptions = checkOfferTypes(textualData?.productData?.contractTypeRadio?.value?.options ?? [], offers);
  const hasSubtitleLayout = filteredOptions[0]?.description;

  const gridLayout = hasSubtitleLayout ? '1fr' : { initial: '1fr', md: '1fr 1fr' };

  let buttonLayout;
  if (hasSubtitleLayout) {
    buttonLayout = { md: `repeat(${filteredOptions.length}, 1fr)` };
  } else if (filteredOptions.length > 2) {
    buttonLayout = '1fr';
  } else {
    buttonLayout = '1fr 1fr';
  }

  return {
    cardRef: cardRef as RefObject<HTMLDivElement | null>,
    isLargeBreakpoint,
    isMediumBreakpoint,
    isExtraLargeBreakpoint,
    isPriceDetailsOpen,
    setIsPriceDetailsOpen,
    shouldShowCarousel,
    shouldShowCarouselIndicators,
    shouldAlignCarouselItems,
    shouldAttachCardRef,
    filteredOptions,
    hasSubtitleLayout,
    gridLayout,
    buttonLayout,
  };
}
